import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useColorSetStore from 'stores/colorSet';
import useCustomBoardStore from 'stores/customBoard';
import useLanguageStore from 'stores/languages';
import useMenuStore from 'stores/menu';
import useScreenTypeStore from 'stores/screenType';
import { PathType, ScreenType } from 'types/common/common.type';
import { MultiLang } from 'types/common/language.type';
import CustomBoardHeader from '../header/CustomBoardHeader';
import styles from './CustomPageContainer.module.scss';
import CardCustomBoardContainer from './card/CardCustomBoardContainer';
import ListCustomBoardContainer from './list/ListCustomBoardContainer';

const CustomPageContainer = () => {
  // hook
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  // store
  const customBoardStore = useCustomBoardStore();
  const menuStore = useMenuStore();
  const languageStore = useLanguageStore();
  const colorSetStore = useColorSetStore();
  const screenTypeStore = useScreenTypeStore();

  const [menuTitle, setMenuTitle] = useState<MultiLang>({
    ko: '',
    en: '',
    ja: '',
    cn: '',
    tw: '',
  });

  const customBoardMenus = useMemo(
    () => menuStore.mainMenus.filter(menu => menu.type === PathType.customBoard && menu.customPageId),
    [menuStore.mainMenus],
  );

  // URL 직접 진입: state → query → 첫 번째 커스텀 보드 순으로 pageId 확보 (롤백 시 이 블록 + redirect 조건 원복)
  const resolvedPageId = useMemo(() => {
    if (state?.pageId) return state.pageId as string;

    const queryPageId = searchParams.get('pageId');
    if (queryPageId) return queryPageId;

    if (customBoardMenus.length > 0) {
      return customBoardMenus[0].customPageId as string;
    }

    return null;
  }, [state?.pageId, searchParams, customBoardMenus]);

  useEffect(() => {
    if (resolvedPageId) return;
    // 메뉴 로딩 전이면 대기
    if (menuStore.mainMenus.length === 0) return;
    // 커스텀 보드 메뉴가 있으면 홈으로 보내지 않음 (pageId 로딩 대기)
    if (customBoardMenus.length > 0) return;

    const url = location.pathname.split('/custom-board')[0];
    navigate(`${url}`, { replace: true });
  }, [resolvedPageId, menuStore.mainMenus.length, customBoardMenus.length]);

  // 메뉴명
  useEffect(() => {
    const findMenu = menuStore.mainMenus.find(menu => menu.customPageId === resolvedPageId);
    if (findMenu) {
      setMenuTitle(findMenu.name);
    }
  }, [resolvedPageId, menuStore.mainMenus]);

  // current page 세팅
  const handleCurrentPage = (customPageId: string) => {
    const findCustomPage = customBoardStore.customPages?.get(customPageId);
    if (findCustomPage) {
      customBoardStore.setCurrentCustomPage(findCustomPage);
    }
  };

  useEffect(() => {
    if (resolvedPageId) {
      handleCurrentPage(resolvedPageId);
    }
  }, [resolvedPageId, customBoardStore.customPages]);

  const isListType = customBoardStore.currentCustomPageType === 'LIST';

  return (
    <div
      className={`${styles.container} ${
        isListType && screenTypeStore.screenType === ScreenType.hori && styles.list_padding_bottom
      }`}
      style={{
        background: colorSetStore.list.bg,
      }}
    >
      <CustomBoardHeader title={menuTitle[languageStore.currentLang]} isList={isListType} />

      {isListType ? <ListCustomBoardContainer /> : <CardCustomBoardContainer />}
    </div>
  );
};
export default CustomPageContainer;
